'use client';

import { type OptimizelyUserInfo, useOptimizely } from 'providers/OptimizelyProvider';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { pushToDataLayer } from '@/app_lib/services/Gtm/utils';
import {
  findOnetrustCookieOptIn,
  findOnetrustCookieOptInAds,
  findOnetrustCookiePerformance,
} from '@/app_lib/services/oneTrust/cookies';
import type { PianoPageConfig } from '@/app_lib/types/Piano';

interface UserContextInterface {
  hasConsent: boolean | null;
  performanceTracking: boolean | null;
  personalizedAds: boolean | null;
  pianoLoadingState: 'loading' | 'loaded' | 'failed';
  pianoPageConfig?: PianoPageConfig;
  setHasConsent: (consent: boolean | null) => void;
  setPerformanceTracking: (value: boolean | null) => void;
  setPersonalizedAds: (value: boolean | null) => void;
  setPianoLoadingState: (state: 'loading' | 'loaded' | 'failed') => void;
  setPianoPageConfig: (config: PianoPageConfig) => void;
  setStopVideoDock: Dispatch<SetStateAction<boolean>>;
  stopVideoDock: boolean;
  userVariants: OptimizelyUserInfo | null;
}

const defaultUserContext: UserContextInterface = {
  hasConsent: null,
  performanceTracking: null,
  personalizedAds: null,
  pianoLoadingState: 'loading',
  pianoPageConfig: undefined,
  setHasConsent: () => {},
  setPerformanceTracking: () => {},
  setPersonalizedAds: () => {},
  setPianoLoadingState: () => {},
  setPianoPageConfig: () => {},
  setStopVideoDock: () => {},
  stopVideoDock: false,
  userVariants: null,
};

interface UserProviderProps {
  children: ReactNode;
  hasLoggedIn: boolean;
}

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const UserContext = createContext<UserContextInterface | undefined>(undefined);

export const UserProvider = ({ children, hasLoggedIn }: UserProviderProps) => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(findOnetrustCookieOptIn());
  const [personalizedAds, setPersonalizedAds] = useState<boolean | null>(findOnetrustCookieOptInAds());
  const [performanceTracking, setPerformanceTracking] = useState<boolean | null>(findOnetrustCookiePerformance());
  const [pianoLoadingState, setPianoLoadingState] = useState<'loading' | 'loaded' | 'failed'>(
    defaultUserContext.pianoLoadingState,
  );
  const [pianoPageConfig, setPianoPageConfig] = useState<PianoPageConfig | undefined>(
    defaultUserContext.pianoPageConfig,
  );
  const [stopVideoDock, setStopVideoDock] = useState(false);
  const { userFlags } = useOptimizely();

  useEffect(() => {
    // biome-ignore lint/style/useNamingConvention: <external data>
    pushToDataLayer({ loginStatusCD: hasLoggedIn ? 'logged-in' : 'guest' });
  }, [hasLoggedIn]);

  const userState = useMemo(
    () => ({
      hasConsent,
      performanceTracking,
      personalizedAds,
      pianoLoadingState,
      pianoPageConfig,
      setHasConsent,
      setPerformanceTracking,
      setPersonalizedAds,
      setPianoLoadingState,
      setPianoPageConfig,
      setStopVideoDock,
      stopVideoDock,
      userVariants: userFlags,
    }),
    [pianoLoadingState, pianoPageConfig, hasConsent, personalizedAds, stopVideoDock, performanceTracking, userFlags],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
